'use client';

import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateConstructionTypeList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/construction-types/search');
};

export const useConstructionTypeDataList = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue) => {
  const isFetchAll = page === undefined;

  const companies = (selectedCompanies || []).map((c) => c.value).join(',');
  const start = isFetchAll ? 0 : page * rowsPerPage;

  const payload = useMemo(() => {
    if (isFetchAll) return null;
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .like(searchKey, searchValue, 'AND')
      .addJoin('company', ['name'])
      .setOrder(['name'], true);

    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companies, isFetchAll]);

  const key = useMemo(() => ['/construction-types/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  return {
    ...normalizeApiResponse(data, error, isLoading),
    refetch: () => mutate(key)
  };
};

export const getConstructionTypeById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.get(`/construction-types/${id}${getTzParam()}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch construction type by ID:', error);
    throw error;
  }
};

export const createConstructionType = async (formData = {}) => {
  try {
    const response = await fleetApi.post(`/construction-types/new${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateConstructionTypeList();
    return response.data;
  } catch (error) {
    console.error('Error creating construction type:', error);
    throw error;
  }
};

export const updateConstructionType = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.put(`/construction-types/edit/${id}${getTzParam()}`, formData);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateConstructionTypeList();
    mutate(`/construction-types/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error updating construction type:', error);
    throw error;
  }
};

export const deleteConstructionType = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.delete(`/construction-types/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateConstructionTypeList();
    return response.data;
  } catch (error) {
    console.error('Error deleting construction type:', error);
    throw error;
  }
};
